import { toFixed } from "@shared/utils/numbers";
import useDebouncedCallback from "@shared/utils/hooks/useDebouncedCallback";
import { useEffect, useState } from "react";

export interface useConnectedAmountPercentInputsProps {
  amountPrecision?: number;
  percentPrecision?: number;
  availableAmount?: number;
  amount?: string;
  percent?: string;
  onAmountChange?: (amount?: string) => void;
  maxPercent?: number;
  defaultPercent?: number;
  useDebounce?: boolean;
  onChange?: () => void;
  onPercentChange?: (percent?: string) => void;
}

const useConnectedAmountPercentInputs = ({
  amountPrecision,
  availableAmount = 0,
  amount,
  percent,
  defaultPercent,
  percentPrecision,
  maxPercent,
  onAmountChange,
  onPercentChange,
  useDebounce,
  onChange,
}: useConnectedAmountPercentInputsProps) => {
  const availableAmountWithPrecision = toFixed(
    availableAmount,
    amountPrecision
  );
  const [localPercent, setLocalPercent] = useState<string | undefined>(
    defaultPercent?.toString()
  );
  const [localAmount, setLocalAmount] = useState<string | undefined>(amount);
  const [amountFocused, setAmountFocused] = useState(true);

  useEffect(() => {
    setLocalAmount(amount);
  }, [amount]);

  useEffect(() => {
    if (percent !== undefined && !isNaN(parseFloat(percent)))
      setLocalPercent(percent);
  }, [percent]);

  const debouncedSetPercentFunc = useDebouncedCallback({
    callback: onPercentChange,
  });
  const debouncedSetAmountFunc = useDebouncedCallback({
    callback: onAmountChange,
  });

  const flushPendingChanges = () => {
    debouncedSetAmountFunc.flush();
    debouncedSetPercentFunc.flush();
  };

  const setPercent = (percent: string | undefined) => {
    setLocalPercent(percent);
    useDebounce ? debouncedSetPercentFunc(percent) : onPercentChange?.(percent);
  };

  const setAmount = (amount: string | undefined) => {
    setLocalAmount(amount);
    useDebounce ? debouncedSetAmountFunc(amount) : onAmountChange?.(amount);
  };

  const _onPercentChange = (percent: string | undefined) => {
    const numberPercent = percent !== undefined ? parseFloat(percent) : 0;
    let amountWithPrecision;
    const percentWithMax =
      maxPercent && numberPercent > maxPercent
        ? maxPercent.toString()
        : percent;
    if (percentWithMax) {
      const amount = (parseFloat(percentWithMax) / 100) * availableAmount;
      amountWithPrecision = toFixed(amount, amountPrecision);
    }
    setAmount((amountWithPrecision || 0).toString());
    setPercent(percentWithMax);
    onChange?.();
  };

  const _onAmountChange = (updatedAmount: string | undefined) => {
    let amountWithMax = updatedAmount;
    if (updatedAmount !== undefined) {
      let percentWithMax = toFixed(
        (parseFloat(updatedAmount) / availableAmountWithPrecision) * 100,
        percentPrecision
      );
      if (maxPercent && percentWithMax > maxPercent) {
        percentWithMax = maxPercent;
        amountWithMax = toFixed(
          (percentWithMax / 100) * availableAmount,
          amountPrecision
        ).toString();
      }
      setPercent(percentWithMax.toString());
    } else {
      setPercent(undefined);
    }
    setAmount(amountWithMax);
    onChange?.();
  };

  const amountInputData = {
    onChange: _onAmountChange,
    value: localAmount,
    precision: amountPrecision,
    onFocus: () => setAmountFocused(true),
    onBlur: flushPendingChanges,
    placeholder: "0",
  };

  const percentInputData = {
    value: localPercent,
    onChange: _onPercentChange,
    precision: percentPrecision,
    onFocus: () => setAmountFocused(false),
    onBlur: flushPendingChanges,
    placeholder: "0",
  };

  return {
    amountInputData,
    percentInputData,
    amountFocused,
  };
};

export default useConnectedAmountPercentInputs;
